import { type VariantProps } from 'class-variance-authority';
import { forwardRef, InputHTMLAttributes } from 'react';
import { inputVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { InputMode } from '@/shared/types/input.types';

export interface InputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /**
   * Input mode hint for mobile keyboards
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
   */
  readonly inputMode?: InputMode;
}

/**
 * Input component with enhanced mobile keyboard support
 *
 * Features:
 * - WCAG 2.1 AA compliant (44px height for touch targets)
 * - Mobile keyboard hints via inputMode
 * - Autocomplete support for better UX
 * - Proper ARIA attributes
 *
 * @example
 * ```tsx
 * <Input type="email" inputMode={InputMode.Email} autoComplete="email" />
 * <Input type="tel" inputMode={InputMode.Tel} autoComplete="tel" />
 * <Input type="text" inputMode={InputMode.Numeric} pattern="[0-9]*" />
 * ```
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputMode, size, variant, ...props }, ref) => {
    // Auto-detect inputMode based on type if not explicitly provided
    let computedInputMode = inputMode;

    if (!inputMode && type) {
      if (type === 'email') {
        computedInputMode = InputMode.Email;
      } else if (type === 'tel') {
        computedInputMode = InputMode.Tel;
      } else if (type === 'url') {
        computedInputMode = InputMode.Url;
      } else if (type === 'search') {
        computedInputMode = InputMode.Search;
      }
    }

    return (
      <input
        type={type}
        inputMode={computedInputMode}
        className={cn(inputVariants({ size, variant }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export { Input };
export { InputMode, InputSize, InputVariant } from '@/shared/types/input.types';
